/**
 * Local authentication utilities for development.
 * Bypasses Auth0 when running on localhost and uses API Gateway emulator tokens.
 */

/**
 * Check if the application is running on localhost.
 */
export const isLocalhost = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
};

/**
 * Get the local API base URL for token fetching.
 */
const getLocalApiUrl = (): string => {
  return 'http://localhost:8001';
};

/**
 * Decode a JWT base64url segment. JWTs use base64url (`-`/`_`, no padding),
 * so we must convert before `atob`, otherwise tokens whose payload contains
 * those characters throw "string not correctly encoded".
 */
export const decodeJwtSegment = (segment: string): string => {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  return atob(padded);
};

/**
 * Fetch a JWT token from the local API Gateway emulator.
 * This token is used for authenticating API requests during local development.
 *
 * @returns Promise<string | null> - The JWT token or null if unavailable
 */
export const getLocalToken = async (): Promise<string | null> => {
  if (!isLocalhost()) {
    return null;
  }

  try {
    const apiPath = getLocalApiUrl();
    const response = await fetch(`${apiPath}/local-token`);

    if (!response.ok) {
      throw new Error(`Failed to get local token: ${response.statusText}`);
    }

    const data = (await response.json()) as { token?: string; access_token?: string };
    return data.token || data.access_token || null;
  } catch (error) {
    console.error('[LOCAL AUTH] Error fetching local token:', error);
    return null;
  }
};

/**
 * Check if a JWT token is valid (not expired).
 * For local dev tokens without exp field, assume valid.
 *
 * @param token - The JWT token to validate
 * @returns boolean - True if the token is valid
 */
export const isTokenValid = (token: string | null | undefined): boolean => {
  if (!token) return false;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;

    const payload = JSON.parse(decodeJwtSegment(parts[1])) as { exp?: number };

    // If token has exp field, check expiration
    if (payload.exp) {
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime;
    }

    // No exp field - assume valid for local dev tokens
    return true;
  } catch (error) {
    console.error('[LOCAL AUTH] Error validating token:', error);
    return false;
  }
};

/**
 * Check if a token is close to expiring (less than specified minutes remaining).
 *
 * @param token - The JWT token to check
 * @param minutesThreshold - Minutes before expiration to consider "close to expiring" (default: 10)
 * @returns boolean - True if token is close to expiring
 */
export const isTokenCloseToExpiry = (token: string | null | undefined, minutesThreshold = 10): boolean => {
  if (!token) return true;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;

    const payload = JSON.parse(decodeJwtSegment(parts[1])) as { exp?: number };

    if (payload.exp) {
      const currentTime = Date.now() / 1000;
      const timeUntilExpiry = payload.exp - currentTime;
      return timeUntilExpiry < minutesThreshold * 60;
    }

    // No exp field - don't refresh
    return false;
  } catch {
    return true;
  }
};

/**
 * Parse user information from a JWT token.
 *
 * @param token - The JWT token to parse
 * @returns User object or null if parsing fails
 */
export const parseUserFromToken = (token: string | null | undefined): LocalUser | null => {
  if (!token) return null;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = JSON.parse(decodeJwtSegment(parts[1])) as TokenPayload;

    return {
      sub: payload.sub || payload.email || 'local-user',
      email: payload.email || 'local-user@localhost',
      name: payload.name || payload.nickname || 'Local User',
      nickname: payload.nickname || payload.email?.split('@')[0] || 'local-user',
      picture: payload.picture,
      ept_roles: payload.ept_roles || [],
      ept_app_metadata: payload.ept_app_metadata,
      userInfo: payload.userInfo,
    };
  } catch (error) {
    console.error('[LOCAL AUTH] Error parsing user from token:', error);
    return null;
  }
};

// Types
export interface LocalUser {
  sub: string;
  email: string;
  name: string;
  nickname: string;
  picture?: string;
  ept_roles: string[];
  ept_app_metadata?: { account_id: number; [key: string]: unknown };
  userInfo?: Record<string, unknown>;
  [key: string]: unknown;
}

interface TokenPayload {
  sub?: string;
  email?: string;
  name?: string;
  nickname?: string;
  picture?: string;
  ept_roles?: string[];
  ept_app_metadata?: { account_id: number; [key: string]: unknown };
  userInfo?: Record<string, unknown>;
  exp?: number;
  [key: string]: unknown;
}

export interface TokenClaims {
  __raw: string;
  sub?: string;
  email?: string;
  name?: string;
  exp?: number;
  ept_roles?: string[];
  [key: string]: unknown;
}

